import React, { createContext, useContext, useState, useCallback } from 'react';
import AssignmentService from '../services/AssignmentService';
import videoCRUDServiceInstance from '../services/VideoCRUDService';

const AssignmentContext = createContext();

export const useAssignmentContext = () => useContext(AssignmentContext);

export const AssignmentProvider = ({ children }) => {
  const [myAssignments, setMyAssignments] = useState([]);
  const [assignedByMe, setAssignedByMe] = useState([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [error, setError] = useState(null);

  const fetchMyAssignments = useCallback(async () => {
    try {
      setLoadingAssignments(true);
      const authUser = JSON.parse(localStorage.getItem('authUser'));
      const authToken = JSON.parse(localStorage.getItem('authToken'));
      
      const response = await AssignmentService.getMyAssignments(
        authUser.email,
        authToken
      );
      
      if (response && response.data) {
        setMyAssignments(response.data);
      }
    } catch (error) {
      console.error("Error fetching assignments:", error);
      setError("Failed to fetch assignments");
    } finally {
      setLoadingAssignments(false);
    }
  }, []);
  
  const fetchAssignedByMe = useCallback(async () => {
    try {
      setLoadingAssignments(true);
      const authUser = JSON.parse(localStorage.getItem('authUser'));
      const authToken = JSON.parse(localStorage.getItem('authToken'));
      
      const response = await AssignmentService.getAssignedByMe(
        authUser.email,
        authToken
      );
      
      if (response && response.data) {
        setAssignedByMe(response.data);
      }
    } catch (error) {
      console.error("Error fetching assignments:", error);
      setError("Failed to fetch assigned by me");
    } finally {
      setLoadingAssignments(false);
    }
  }, []);
  
  const fetchAssignedVideos = useCallback(async () => {
    try {
      setLoadingAssignments(true);
      const authUser = JSON.parse(localStorage.getItem('authUser'));
      const authToken = JSON.parse(localStorage.getItem('authToken'));
      
      const response = await AssignmentService.getMyAssignments(
        authUser.email,
        authToken
      );
      
      if (response && response.data) {
        // We need to fetch the actual video data for each assignment
        const assignmentData = response.data;
        const videoIds = assignmentData.map(a => a.videoId);
        
        if (videoIds.length > 0) {
          const videosResponse = await videoCRUDServiceInstance.getVideoDataOfIds(
            videoIds.join(','),
            authUser.email,
            authToken
          );
          
          if (videosResponse && videosResponse.data) {
            // Add assignment info to video data
            const videos = videosResponse.data.map(video => {
              const videoAssignments = assignmentData.filter(a => a.videoId === video.id);
              return {
                ...video,
                assignments: videoAssignments
              };
            });
            
            // Return both the enhanced videos and the raw assignment data
            return {
              videos,
              assignmentData
            };
          }
        }
        
        // If no videos found, still return the assignment data
        return {
          videos: [],
          assignmentData
        };
      }
      
      return {
        videos: [],
        assignmentData: []
      };
    } catch (error) {
      console.error("Error fetching assigned videos:", error);
      setError("Failed to fetch assigned videos");
      return {
        videos: [],
        assignmentData: []
      };
    } finally {
      setLoadingAssignments(false);
    }
  }, []);

  const assignVideo = useCallback(async (assignment) => {
    try {
      setLoadingAssignments(true);
      const authUser = JSON.parse(localStorage.getItem('authUser'));
      const authToken = JSON.parse(localStorage.getItem('authToken'));
      
      const response = await AssignmentService.createAssignment(
        assignment,
        authUser.email,
        authToken
      );
      
      // Refresh assignments after adding a new one
      fetchMyAssignments();
      fetchAssignedByMe();
      
      return response;
    } catch (error) {
      console.error("Error assigning video:", error);
      setError("Failed to assign video");
      throw error;
    } finally {
      setLoadingAssignments(false);
    }
  }, [fetchMyAssignments, fetchAssignedByMe]);

  const value = {
    myAssignments,
    assignedByMe,
    loadingAssignments,
    error,
    fetchMyAssignments,
    fetchAssignedByMe,
    fetchAssignedVideos,
    assignVideo,
  };

  return (
    <AssignmentContext.Provider value={value}>
      {children}
    </AssignmentContext.Provider>
  );
};